import { Flex, Input, Text } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DateUnit } from '~/types/Shared'
import {
  allHaveValue,
  hasValue,
  isNumeric,
  noneHaveValue,
} from '~/utils/common'
import { clamp, isInRange } from '~/utils/math'
import { tryGetLocaleNumericString } from '~/utils/numberUtils'

import {
  getCurrentYear,
  getDayInMonthOrMax,
  tryGetRealMonth,
} from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const MAX_CHARS_MONTHS_DAYS = 2
const MAX_CHARS_YEARS = 4
const MIN_MONTHS = 1
const MAX_MONTHS = 12
const MIN_DAYS = 1
const MAX_DAYS = 31
const MIN_YEARS = 2000
const MAX_YEARS = getCurrentYear()

interface DateInputProps {
  /** Names the fields, e.g. "After" gives "After month", "After day". */
  label: string
  value?: Date | undefined
  mountsFocused?: boolean
  onDateSet?: (date?: Date) => void
}

const getExternalStringValuesOrDefault = (value: Date | undefined) => {
  const externalMonths = tryGetLocaleNumericString(tryGetRealMonth(value)) ?? ''
  const externalDays = tryGetLocaleNumericString(value?.getDate()) ?? ''
  const externalYears = tryGetLocaleNumericString(value?.getFullYear()) ?? ''

  return { externalMonths, externalDays, externalYears }
}

export const DateInput = ({
  label,
  value: externalValue,
  mountsFocused,
  onDateSet,
}: DateInputProps) => {
  const { t } = useTranslation()
  const { externalMonths, externalDays, externalYears } =
    getExternalStringValuesOrDefault(externalValue)

  const [months, _setMonths] = React.useState<string>(externalMonths)
  const [days, _setDays] = React.useState<string>(externalDays)
  const [years, _setYears] = React.useState<string>(externalYears)

  const [elementNeedsSelection, setElementNeedsSelection] =
    React.useState<HTMLInputElement>()
  const monthsInputRef = React.useRef<HTMLInputElement>(null)
  const daysInputRef = React.useRef<HTMLInputElement>(null)
  const yearsInputRef = React.useRef<HTMLInputElement>(null)

  const getStringAndNumericValues = (value: string | number) => {
    const valueIsString = typeof value === 'string'
    const stringValue = valueIsString ? value : value.toString()
    const numericValue = isNumeric(stringValue) ? parseInt(stringValue) : NaN

    return { stringValue, numericValue }
  }

  const tryGetCorrectedDays = (targetDate: {
    targetDay?: string
    targetMonth?: string
    targetYear?: string
  }) => {
    const { targetDay, targetMonth, targetYear } = targetDate
    //all three must be set to validate
    if (noneHaveValue([targetDay, targetMonth, targetYear])) {
      return
    }

    const validatedDay = getDayInMonthOrMax(
      targetDay!,
      targetMonth!,
      targetYear!,
    )?.toString()
    if (!hasValue(validatedDay)) {
      return
    }

    let formattedDay = validatedDay!.toString()
    if (formattedDay.length < MAX_CHARS_MONTHS_DAYS) {
      formattedDay = `0${formattedDay}`
    }

    return formattedDay
  }

  const setMonths = (newMonths: number | string, forceEval = false) => {
    const { stringValue, numericValue } = getStringAndNumericValues(newMonths)

    //only numbers
    if (hasValue(stringValue) && isNaN(numericValue)) {
      setElementNeedsSelection(monthsInputRef.current!)
      return
    }

    //only two digits
    if (stringValue.length > MAX_CHARS_MONTHS_DAYS) {
      return
    }

    let formattedValue = stringValue
    if (hasValue(formattedValue)) {
      if (isInRange(numericValue, 2, 9)) {
        formattedValue = `0${numericValue}`
      }

      if (numericValue > MAX_MONTHS) {
        formattedValue = MAX_MONTHS.toString()
      }

      if (forceEval) {
        const adjustedValue = clamp(numericValue, MIN_MONTHS, MAX_MONTHS)
        formattedValue = adjustedValue.toLocaleString('en-US', {
          minimumIntegerDigits: MAX_CHARS_MONTHS_DAYS,
          useGrouping: false,
        })
      }
    }

    if (formattedValue === months) {
      return
    }

    _setMonths(formattedValue)
    if (formattedValue.length === MAX_CHARS_MONTHS_DAYS) {
      daysInputRef.current?.focus()
    }
  }

  const setDays = (newDays: number | string, forceEval = false) => {
    const { stringValue, numericValue } = getStringAndNumericValues(newDays)
    //only numbers
    if (hasValue(stringValue) && isNaN(numericValue)) {
      setElementNeedsSelection(daysInputRef.current!)
      return
    }

    //only two digits
    if (stringValue.length > MAX_CHARS_MONTHS_DAYS) {
      return
    }

    let formattedValue = stringValue

    if (hasValue(formattedValue)) {
      if (isInRange(numericValue, 4, 9)) {
        formattedValue = `0${numericValue}`
      }

      if (forceEval) {
        const adjustedValue = clamp(numericValue, MIN_DAYS, MAX_DAYS)

        formattedValue = adjustedValue.toLocaleString('en-US', {
          minimumIntegerDigits: MAX_CHARS_MONTHS_DAYS,
          useGrouping: false,
        })
      }
    }

    if (formattedValue === days) {
      return
    }

    _setDays(formattedValue)
    if (formattedValue.length === MAX_CHARS_MONTHS_DAYS) {
      yearsInputRef.current?.focus()
    }
  }

  const setYears = (newYears: number | string, forceEval = false) => {
    const { stringValue, numericValue } = getStringAndNumericValues(newYears)

    //only numbers
    if (hasValue(stringValue) && isNaN(numericValue)) {
      setElementNeedsSelection(yearsInputRef.current!)
      return
    }

    //only four digits
    if (stringValue.length > MAX_CHARS_YEARS) {
      return
    }

    let formattedValue = stringValue

    if (hasValue(formattedValue)) {
      if (
        formattedValue.length === MAX_CHARS_YEARS &&
        numericValue < MIN_YEARS
      ) {
        formattedValue = MIN_YEARS.toString()
      }

      if (numericValue > MAX_YEARS) {
        formattedValue = MAX_YEARS.toString()
      }

      if (forceEval) {
        const adjustedValue = clamp(numericValue, MIN_YEARS, MAX_YEARS)
        formattedValue = adjustedValue.toLocaleString('en-US', {
          minimumIntegerDigits: MAX_CHARS_YEARS,
          useGrouping: false,
        })
      }
    }

    if (formattedValue === years) {
      return
    }

    _setYears(formattedValue)
  }

  //attempts to correct the "days" field when any of the date fields change
  useEffect(() => {
    const isBlank = noneHaveValue([days, months, years])

    const isAnyFieldIncomplete =
      days.length < MAX_CHARS_MONTHS_DAYS ||
      months.length < MAX_CHARS_MONTHS_DAYS ||
      years.length < MAX_CHARS_YEARS

    const isComplete =
      !isAnyFieldIncomplete && allHaveValue([days, months, years])

    if (isBlank) {
      onDateSet?.()
      return
    }

    if (!isComplete) {
      return
    }

    const correctedDays = tryGetCorrectedDays({
      targetDay: days,
      targetMonth: months,
      targetYear: years,
    })

    const date = new Date(`${months}/${correctedDays}/${years}`)
    onDateSet?.(date)
  }, [days, months, years])

  useEffect(() => {
    if (!elementNeedsSelection) {
      return
    }

    elementNeedsSelection.select()
    setElementNeedsSelection(undefined)
  }, [elementNeedsSelection])

  //when value is set externally, update all input fields
  useEffect(() => {
    if (!externalValue) {
      //for this to have triggered, there had to be an external value in the first place, safe to wipe away existing state values
      _setMonths('')
      _setDays('')
      _setYears('')
      return
    }

    const dateMonths = (externalValue.getMonth() + 1).toString() //months are 0-indexed because reasons
    const dateDays = externalValue.getDate().toString()
    const dateYears = externalValue.getFullYear().toString()

    handleSetDateValue(dateMonths, DateUnit.Months, true)
    handleSetDateValue(dateDays, DateUnit.Days, true)
    handleSetDateValue(dateYears, DateUnit.Years, true)
  }, [externalValue])

  const handleSetDateValue = (
    e: React.ChangeEvent<HTMLInputElement> | string,
    unit: DateUnit,
    checkLength = false,
  ) => {
    const valueIsString = typeof e === 'string'
    const value = valueIsString ? e : e.target.value

    switch (unit) {
      case DateUnit.Months:
        setMonths(value, checkLength)
        break
      case DateUnit.Days:
        setDays(value, checkLength)
        break
      case DateUnit.Years:
        setYears(value, checkLength)
        break
    }
  }

  const handleInputBlur = (timeUnit: DateUnit) => {
    let inputElement: HTMLInputElement
    switch (timeUnit) {
      case DateUnit.Months:
        inputElement = monthsInputRef.current!
        break
      case DateUnit.Days:
        inputElement = daysInputRef.current!
        break
      case DateUnit.Years:
        inputElement = yearsInputRef.current!
        break
    }

    handleSetDateValue(inputElement.value, timeUnit, true)
  }

  const tryUnfocusAllInputs = () => {
    monthsInputRef.current?.blur()
    daysInputRef.current?.blur()
    yearsInputRef.current?.blur()
  }

  useEffect(() => {
    if (
      !allHaveValue([
        monthsInputRef.current,
        daysInputRef.current,
        yearsInputRef.current,
      ])
    ) {
      return
    }

    if (!mountsFocused) {
      //unfocus all inputs
      tryUnfocusAllInputs()

      return
    }

    monthsInputRef.current!.focus()
    setElementNeedsSelection(monthsInputRef.current!)
  }, [
    mountsFocused,
    monthsInputRef.current,
    daysInputRef.current,
    yearsInputRef.current,
  ])

  const monthOrDaySet =
    months.length === MAX_CHARS_MONTHS_DAYS ||
    days.length === MAX_CHARS_MONTHS_DAYS
  const dayOrYearSet =
    days.length === MAX_CHARS_MONTHS_DAYS || years.length === MAX_CHARS_YEARS

  return (
    <Flex
      position="relative"
      width="full"
      height="2.5625rem"
      borderRadius="0.5rem"
      border="1px solid"
      borderColor="border.default"
      alignItems="center"
      justifyContent="flex-start"
      px="0.5rem"
    >
      <Input
        ref={monthsInputRef}
        border="none"
        height="90%"
        width="1.825rem"
        p="0rem"
        placeholder={t('form.placeholder.dateMonth')}
        aria-label={t('dateTimeRangeModal.monthLabel', '{{label}} month', {
          label,
        })}
        {...textStyles[textStyleKeys.Body1]}
        textAlign="center"
        cursor="pointer"
        _focus={{ cursor: 'auto' }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
        }}
        value={months}
        onFocus={(e) => e.target.select()}
        onBlur={() => handleInputBlur(DateUnit.Months)}
        onChange={(e) => handleSetDateValue(e, DateUnit.Months)}
      />
      <Text
        {...textStyles[textStyleKeys.Body1]}
        color={monthOrDaySet ? 'fg.default' : 'border.default'}
      >
        /
      </Text>
      <Input
        ref={daysInputRef}
        border="none"
        height="90%"
        width="1.75rem"
        p="0rem"
        {...textStyles[textStyleKeys.Body1]}
        placeholder={t('form.placeholder.dateDay')}
        aria-label={t('dateTimeRangeModal.dayLabel', '{{label}} day', {
          label,
        })}
        textAlign="center"
        cursor="pointer"
        _focus={{ cursor: 'auto' }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
        }}
        value={days}
        onFocus={(e) => e.target.select()}
        onBlur={() => handleInputBlur(DateUnit.Days)}
        onChange={(e) => handleSetDateValue(e, DateUnit.Days)}
      />
      <Text
        {...textStyles[textStyleKeys.Body1]}
        color={dayOrYearSet ? 'fg.default' : 'border.default'}
      >
        /
      </Text>
      <Input
        ref={yearsInputRef}
        border="none"
        height="90%"
        p="0rem"
        {...textStyles[textStyleKeys.Body1]}
        width="3rem"
        placeholder={t('form.placeholder.dateYear')}
        aria-label={t('dateTimeRangeModal.yearLabel', '{{label}} year', {
          label,
        })}
        textAlign="center"
        cursor="pointer"
        _focus={{ cursor: 'auto' }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
        }}
        value={years}
        onFocus={(e) => e.target.select()}
        onChange={(e) => handleSetDateValue(e, DateUnit.Years)}
      />
    </Flex>
  )
}
